import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/macro';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  TextField,
  Typography } from
'@mui/material/';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { interpolateOrgs } from '../utils';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { NewApiBaseUrl } from "../consts/new-api-base-url";
import useHttpApiNew from "../hooks/useHttpApiNew";
import { useTheme } from "@mui/material";
import SvgCustomIcon from "../components/icons/SvgCustomIcon";
import { getBrandingUrls, LOGO_FRED_POWERED_BY_URL } from '../config/market';
import { getInitSnapshot, saveInitSnapshot } from '../offline/init-snapshot';
import { warmupLogoCache } from '../offline/logo-cache-warmup';
import {
  syncOfflineUserDirectory,
  findOfflineDirectoryUserByUsername,
  mapDirectoryRowToInitUser,
  getOfflineOrganizationFormConfig,
  resolveOfflineFormOrganizationId } from
'../offline/offline-data-sync';
import { saveRefreshToken } from '../offline/session-store';
import useOnlineStatus from '../hooks/useOnlineStatus';
import {
  getOfflineUsers,
  verifyOfflineCredentials,
  saveOfflineUser,
  hashPasswordForOffline } from
'../offline/offline-users-store';
import { requestStoragePersistence } from '../offline/storage-persist';
import { isDeviceProvisioned } from '../offline/device-provision-meta';
import { hasValidPersistedSession, readPersistedAdamsUser } from '../offline/offline-session-utils';
import { buildConfigLocalizedUrl, getConfigLocaleHeaders } from '../utils/config-localization';

const BRAND = getBrandingUrls();

const loginUrl = new URL(`${NewApiBaseUrl}/Auth/Login`);
const initUrl = new URL(`${NewApiBaseUrl}/Auth/Init`);

const containerImage = `url('assets/Sfondo_ADaMS_NEW_version.jpg')`;

export default function Login({ isMobile, setThemeMode }) {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [lastLoginDate, setLastLoginDate] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [offlineUsers, setOfflineUsers] = useState([]);
  const [showOfflineUserSelect, setShowOfflineUserSelect] = useState(false);

  const isOnline = useOnlineStatus();

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const theme = useTheme();

  const { _ } = useLingui();
  const { request } = useHttpApiNew();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  useEffect(() => {
    if (!isOnline) {
      getOfflineUsers().then((users) => {
        setOfflineUsers(users);
        if (users.length > 0) {
          setShowOfflineUserSelect(true);
        }
      });
    } else {
      setOfflineUsers([]);
      setShowOfflineUserSelect(false);
    }
  }, [isOnline]);

  useEffect(() => {
    if (!isOnline) return;
    warmupLogoCache().catch(() => {});
  }, [isOnline]);

  const handleOfflineUserSelect = (user) => {
    setUsername(user.username);
    setShowOfflineUserSelect(false);
  };

  const handleSubmit = async () => {
    if (!username || !password) {
      setSnackbarMessage(_(msg`Please enter username and password`));
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);

    const addDaysToToday = (days) => {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date;
    };

    if (!isOnline) {
      try {
        if (!(await isDeviceProvisioned())) {
          setSnackbarMessage(
            _(
              msg`This device is not prepared for offline use. An administrator must prepare it while online (menu: Prepare device for offline use).`
            )
          );
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        const { valid, user: offlineUser } = await verifyOfflineCredentials(username, password);
        if (!valid) {
          setSnackbarMessage(_(msg`Invalid offline credentials`));
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        const snapshot = await getInitSnapshot();
        const init = snapshot?.init;
        if (!init?.configuration) {
          setSnackbarMessage(_(msg`No offline data available. Please login online first.`));
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        const directoryRow = await findOfflineDirectoryUserByUsername(offlineUser.username);
        const initUser = directoryRow ?
        mapDirectoryRowToInitUser(directoryRow, init.user) :
        null;
        if (!initUser) {
          setSnackbarMessage(
            _(msg`No offline user profile for this account. Log in online as an admin once so the user list syncs, then try again.`)
          );
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        const orgIdForForm = resolveOfflineFormOrganizationId(initUser);
        if (orgIdForForm == null) {
          setSnackbarMessage(
            _(msg`Your profile has no organization for offline use. Log in online once, then try again.`)
          );
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        const orgOffline = await getOfflineOrganizationFormConfig(orgIdForForm);
        const hasOrgForm =
        orgOffline &&
        orgOffline.configuration != null &&
        orgOffline.defaults != null;

        if (!hasOrgForm) {
          setSnackbarMessage(
            _(msg`Offline form for your organization was not found. Stay online once after login so organization configs sync, then try again.`)
          );
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          setIsLoading(false);
          return;
        }

        let initForSession = { ...init, user: initUser };
        initForSession = {
          ...initForSession,
          configuration: orgOffline.configuration,
          defaults: orgOffline.defaults
        };
        await saveInitSnapshot(initForSession).catch(() => {});

        const timestamp = addDaysToToday(10).getTime();
        const displayName =
        offlineUser.displayName ||
        `${initUser.name} ${initUser.surname}`.trim() ||
        offlineUser.username;
        const adamsUser = {
          ...initUser,
          username: initUser.username,
          displayName,
          token: '__offline__',
          timestamp,
          init: initForSession,
          isOfflineSession: true
        };
        localStorage.setItem('adams_user', JSON.stringify(adamsUser));

        requestStoragePersistence().catch(() => {});

        setSnackbarMessage(_(msg`Offline login successful!`));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        setTimeout(() => {
          navigate('/home', {
            state: {
              init: initForSession,
              isOfflineSession: true,
              token: '__offline__'
            }
          });
        }, 400);
      } catch (err) {

        setSnackbarMessage(_(msg`Offline login failed`));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    const authdata = window.btoa(`${username}:${password}`);
    try {
      const response = await request(loginUrl, 'POST', null, { Authorization: `Basic ${authdata}` });
      if (response.responseAnyError) {

        setSnackbarMessage(_(msg`Invalid credentials or authentication error`));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      // Login ok
      const data = response.responseData;
      setToken(data.token);
      setLastLoginDate(data.last_login_date);

      const timestamp = addDaysToToday(10).getTime();
      const user = { timestamp };

      localStorage.setItem('adams_user', JSON.stringify(user));
      localStorage.setItem('token', data.token);
      if (data.refreshToken) {
        saveRefreshToken(data.refreshToken).catch(() => {});
      }

      await saveOfflineUser({
        username,
        displayName: username,
        passwordHash: await hashPasswordForOffline(password)
      }).catch(() => {});

      requestStoragePersistence().catch(() => {});

      setSnackbarMessage(_(msg`Login successful!`));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {

      setSnackbarMessage(_(msg`Server connection error`));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getInit = useCallback(async () => {

    const localizedInitUrl = buildConfigLocalizedUrl(initUrl);
    const response = await request(localizedInitUrl, 'GET', null, getConfigLocaleHeaders({
      Authorization: `Bearer ${token}`,
    }));
    if (response.responseData) {
      let { error: initError, error_code, result, organizations, ...init } = response.responseData;
      init = { ...init, organizations: interpolateOrgs(organizations) };
      const user = init.user;
      setThemeMode(user.theme);

      let adamsUser = JSON.parse(localStorage.getItem('adams_user'));
      adamsUser = { ...adamsUser, ...user, lastLoginDate, init, token };
      localStorage.setItem('adams_user', JSON.stringify(adamsUser));
      saveInitSnapshot(init).catch(() => {});

      if (typeof navigator !== 'undefined' && navigator.onLine) {
        syncOfflineUserDirectory(token).catch(() => {});
      }

      requestStoragePersistence().catch(() => {});

      navigate('/home', { state: { token, init, lastLoginDate } });
    } else {
      setSnackbarMessage(_(msg`Error loading initial data`));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }

  }, [token, lastLoginDate, navigate, setThemeMode, request]);

  useEffect(() => {
    const adamsUser = readPersistedAdamsUser();
    if (hasValidPersistedSession(adamsUser)) {
      navigate('/home', {
        replace: true,
        state: {
          token: adamsUser.token,
          init: adamsUser.init,
          lastLoginDate: adamsUser.lastLoginDate,
          isOfflineSession: adamsUser.isOfflineSession,
        },
      });
      return;
    }
    if (adamsUser) {
      localStorage.removeItem('adams_user');
    }
  }, [navigate]);

  useEffect(() => {
    if (token && lastLoginDate) getInit();
  }, [token, lastLoginDate, getInit]);

  return (
    <Box
      sx={{
        backgroundImage: `${containerImage}`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        px: 2,
        position: 'relative'
      }}>
      
            <LanguageSwitcher />

            {!isOnline &&
      <Alert
        severity="info"
        sx={{
          position: 'absolute',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          maxWidth: 'min(480px, 92vw)'
        }}>
        
                    {_(msg`You are offline. Login with saved credentials.`)}
                </Alert>
      }

            {/* Main Card */}
            <Container
        maxWidth="xs"
        sx={{
          width: '100%',
          backgroundColor: 'white',
          boxShadow: '-8px 0px 20px rgba(0, 0, 0, 0.2)',
          borderRadius: 10,
          py: 3,
          px: 3
        }}>
        
                <Box sx={{ p: 2 }}>
                    <img
            src={BRAND.loginPrimary}
            style={{
              height: isMobile ? 60 : 80,
              width: '100%',
              objectFit: 'contain'
            }}
            alt={_(msg`ADaMS Logo`)} />
          
                </Box>
                {/* Offline login 
          {!isOnline && offlineUsers.length > 0 && showOfflineUserSelect && (
             <Box sx={{ mb: 2, px: 1 }}>
                 <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                     {_(msg`Quick login:`)}
                 </Typography>
                 {offlineUsers.map((u) => (
                     <Button
                         key={u.username}
                         variant="outlined"
                         size="small"
                         fullWidth
                         sx={{ mb: 0.5, justifyContent: 'flex-start' }}
                         onClick={() => handleOfflineUserSelect(u)}
                     >
                         {u.displayName || u.username}
                     </Button>
                 ))}
             </Box>
          )}
             */}
                <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}>
          
                    <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            sx={{
              background: '#EDEDED',
              borderRadius: 4,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              }
            }}
            label={_(msg`Username`)}
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)} />
          
                    <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label={_(msg`Password`)}
            sx={{
              background: '#EDEDED',
              borderRadius: 4,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              }
            }}
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment:
              <InputAdornment position="end">
                                    <IconButton
                  type="button"
                  aria-label={_(msg`toggle password visibility`)}
                  onClick={handleClickShowPassword}
                  edge="end">
                  
                                        {showPassword ? <VisibilityOff /> : <SvgCustomIcon name={'visibility'} color={theme.palette.newColors.gray} />}
                                    </IconButton>
                                </InputAdornment>

            }} />
          
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                        <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  color: 'white',
                  background: theme.palette.newColors.pinkGradient,
                  borderRadius: '12px',
                  py: 1.5,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    background: theme.palette.newColors.pinkGradient,
                    transform: 'scale(1.03)'
                  },
                  '&:disabled': {
                    background: 'lightgray',
                    color: '#666',
                    cursor: 'not-allowed'
                  }
                }}>
                
                            {isLoading ? _(msg`Accessing...`) : _(msg`Login`)}
                        </Button>
                    </Grid>
                    </Grid>
                </Box>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} sx={{ textAlign: 'center' }}>
                        <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? 16 : 14,
                color: 'black'
              }}>
              
                            {_(msg`Forgot`)}{' '}
                            <Link
                href="/forgot-pwd"
                sx={{
                  color: theme.palette.newColors.pink,
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' }
                }}>
                
                                {_(msg`password?`)}
                            </Link>
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sx={{ textAlign: 'center' }}>
                        <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? 16 : 14,
                color: 'black'
              }}>
              
                            {_(msg`Don't have an account?`)}{' '}
                            <Link
                href="/signup"
                sx={{
                  color: theme.palette.newColors.pink,
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' }
                }}>
                
                                {_(msg`Sign Up`)}
                            </Link>
                        </Typography>
                    </Grid>
                </Grid>

                <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{
            mt: 1,
            mb: 1
          }}>
          
                    <Grid
            item
            xs={6}
            sm={4}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            
                        <Box
              component="img"
              src={BRAND.loginFlag}
              alt={_(msg`Micronesia Flag`)}
              sx={{
                width: '100%',
                maxWidth: 100,
                objectFit: 'contain'
              }} />
            
                    </Grid>

                    <Grid
            item
            xs={6}
            sm={4}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            
                        <Box
              component="img"
              src={BRAND.loginSeal}
              alt={_(msg`Micronesia Seal`)}
              sx={{
                width: '100%',
                maxWidth: 80,
                objectFit: 'contain'
              }} />
            
                    </Grid>
                </Grid>
            </Container>

            {/* Footer — stesso markup di prima della refactor multi-mercato */}
            <Box
        component="a"
        href="https://www.fredeng.eu/"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          textDecoration: 'none'
        }}>
        
                <Typography variant="body2" sx={{ color: 'white' }}>{_(msg`Powered by`)}</Typography>
                <Box
          component="img"
          src={LOGO_FRED_POWERED_BY_URL}
          alt={_(msg`Fred logo`)}
          sx={{
            height: isMobile ? 64 : 96,
            width: 'auto',
            objectFit: 'contain'
          }} />
        
            </Box>

            {/* Snackbar */}
            <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        
                <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}>
          
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>);

}